import React, { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { ia, iaRa, inRn } from 'lib/helpers/utility';

import { modulesState, permissions } from 'components/state';
import {
  calcInvoiceDiscountFromProducts,
  calculateInvoiceTaxFromItems
} from 'components/practice/NewAppointment/components/Sections/Pricing/components/ProceduresProduct/components/SelectedItems/helpers';

import ReceiptTable from '../ReceiptTable/ReceiptTable';
import { calculateTotals } from '../ReceiptSummary/lib/helpers';

import { ITEMS_TABLE_COLUMNS } from './lib/columnDefs';

const ReceiptItems = ({ index, transaction, hasMultipleTransactions }) => {
  const userPermissions = useRecoilValue(permissions);
  const practiceModules = useRecoilValue(modulesState);

  const { invoice, isPatient, date_of_service } = transaction || {};

  const items = [
    ...iaRa(invoice?.procedures),
    ...iaRa(invoice?.products),
    ...iaRa(invoice?.packages)
  ];

  const hasItems = ia(items);

  const columnDefs = useMemo(
    () => ITEMS_TABLE_COLUMNS(userPermissions, isPatient, hasItems, practiceModules),
    [userPermissions, isPatient, hasItems, practiceModules]
  );

  const amountRefunded = inRn(transaction?.amount_refunded);
  const amountPaid = inRn(transaction?.amount_paid_self) - inRn(transaction?.surcharge_amount);
  const amountTotal = inRn(transaction?.amount_total);

  const modifiedItems = iaRa(items).map((item) => {
    const { pt_balance, total_cost_cents, amount_cents, discount, type, invoiceId } = item || {};

    const price = pt_balance ?? total_cost_cents ?? amount_cents ?? 0;
    let discountValue = discount?.amount_cents || 0;

    let taxAmount = calculateInvoiceTaxFromItems({ mergedItems: [item] });

    const isService = type || invoiceId;

    if (item?.products?.length || item?.procedures?.length) {
      taxAmount = calculateInvoiceTaxFromItems(item);

      const invoiceDiscount = calcInvoiceDiscountFromProducts(item);
      discountValue = invoiceDiscount?.amount_cents || 0;
    }

    const qty =
      (isService ? (item?.quantity ?? item?.units ?? item?.sales_count) : item?.sales_count) || 1;

    const total = price * qty;

    return {
      ...item,
      price,
      quantity: qty,
      discount: discountValue,
      tax: taxAmount,
      total
    };
  });

  const noItems = !hasItems ? [{ charge: amountPaid + amountRefunded || amountTotal }] : [];

  const totals = useMemo(() => {
    const calculated = calculateTotals([transaction]);
    const isWriteoff = transaction?.type === 'writeoff';

    return {
      totalPaid: amountPaid + amountRefunded,
      previousPayments: calculated.totalAmountPaid - (!isWriteoff ? amountPaid : 0),
      remainingBalance: calculated.totalBalance,
      totalAmount: calculated.totalAmount
    };
  }, [transaction, amountPaid, amountRefunded]);

  return (
    <ReceiptTable
      index={index}
      items={hasItems ? modifiedItems : noItems}
      columns={columnDefs}
      footer={hasItems}
      totals={totals}
      dateOfService={date_of_service}
      hasMultipleTransactions={hasMultipleTransactions}
    />
  );
};

export default ReceiptItems;
